/**
 * API utility functions for QuickBooks Connect UI
 */

interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
}

interface OAuthStartResponse {
  consentUrl: string
}

interface OAuthCompleteResponse {
  connectionId: string
  sourceId: string
  destinationId: string
  companyName?: string
}

interface SyncTriggerResponse {
  jobId: string
}

interface SyncStatusResponse {
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress?: number
  recordsExtracted?: number
  recordsLoaded?: number
  failureReason?: string
}

class ApiClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
  }

  private buildUrl(path: string): string {
    // Ensure leading slash
    let p = path.startsWith('/') ? path : `/${path}`
    // Remove any leading /api from the path to avoid double /api
    if (p.startsWith('/api/')) {
      p = p.slice(4) // Remove leading '/api'
    }
    // Join base and path, avoiding double slashes
    const base = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl
    return `${base}${p}`
  }

  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint)
    
    const headers = new Headers(options.headers)
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
    if (!headers.has('X-Tenant-ID')) {
      headers.set('X-Tenant-ID', 'default')
    }

    const defaultOptions: RequestInit = {
      headers,
      credentials: 'include', // Include cookies for CSRF tokens
      ...options
    }

    try {
      const response = await fetch(url, defaultOptions)
      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          message: data.message || `HTTP ${response.status}: ${response.statusText}`
        }
      }

      return {
        success: true,
        data: data
      }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error occurred'
      }
    }
  }

  /**
   * Start QuickBooks OAuth flow
   */
  async startOAuth(tenantId?: string): Promise<ApiResponse<OAuthStartResponse>> {
    return this.request<OAuthStartResponse>('/quickbooks/oauth/start', {
      method: 'POST',
      body: JSON.stringify({ tenantId: tenantId || 'default' })
    })
  }

  /**
   * Complete QuickBooks OAuth flow
   */
  async completeOAuth(params: {
    code: string
    state: string
    realmId?: string
  }): Promise<ApiResponse<OAuthCompleteResponse>> {
    return this.request<OAuthCompleteResponse>('/quickbooks/oauth/complete', {
      method: 'POST',
      body: JSON.stringify(params)
    })
  }

  /**
   * Trigger initial sync
   */
  async triggerSync(connectionId: string): Promise<ApiResponse<SyncTriggerResponse>> {
    return this.request<SyncTriggerResponse>('/quickbooks/sync/trigger', {
      method: 'POST',
      body: JSON.stringify({ connectionId })
    })
  }

  /**
   * Check sync status
   */
  async getSyncStatus(jobId: string): Promise<ApiResponse<SyncStatusResponse>> {
    return this.request<SyncStatusResponse>(`/quickbooks/sync/status/${jobId}`)
  }

  /**
   * Get connection health status
   */
  async getHealthCheck(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return this.request('/health')
  }
}

// Export singleton instance
export const api = new ApiClient()

// Export types for use in components
export type { 
  ApiResponse, 
  OAuthStartResponse, 
  OAuthCompleteResponse, 
  SyncTriggerResponse, 
  SyncStatusResponse 
}